import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import type { SpinLineage } from '../../../Generated/SpinLineage.ts'
import type { SpinLineageStep } from '../../../Generated/SpinLineageStep.ts'
import { fetchSpinLineage } from '../../../Repository/Visualization/SpinMeta.js'
import { BaseModal } from '../../Modal/BaseModal.js'
import { BlastSourceIcon } from '../../Component/BlastSource/BlastSourceIcon.js'
import { formatDate, parseDate } from '../../../Util/DateHandling.js'
import { RANK_LABELS, cleanName, isItalicRank } from './spinSummary.logic.js'
import { changeNotes, driverLabel, effectLabel, headlineOf, resolutionOf, sourceDiffs, type SourceDiff } from './spinLineage.logic.js'

const STATUS_LABEL: Record<SourceDiff['status'], string> = {
  changed: 'Changed',
  deeper: 'Resolved deeper',
  shallower: 'Resolution decreased',
  unchanged: 'Unchanged'
}

const STATUS_CLASS: Record<SourceDiff['status'], string> = {
  changed: 'bg-sky-50 text-sky-800',
  deeper: 'bg-emerald-50 text-emerald-800',
  shallower: 'bg-amber-50 text-amber-900',
  unchanged: 'bg-gray-50 text-gray-600'
}

const Name: React.FC<{ v?: { rank: number; name: string } }> = ({ v }) =>
  v ? (
    <span>
      <span className="text-[10px] text-gray-400 mr-1">{RANK_LABELS[v.rank]}</span>
      <span className={isItalicRank(v.rank) ? 'italic' : ''}>{v.name}</span>
    </span>
  ) : (
    <span className="text-gray-300">—</span>
  )

const StepCard: React.FC<{ step: SpinLineageStep; version: number; current: boolean; selected: boolean; onSelect: () => void }> = ({
  step,
  version,
  current,
  selected,
  onSelect
}) => {
  const head = headlineOf(step)
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left rounded border p-3 ${selected ? 'border-emerald-400 bg-emerald-50' : 'bg-white hover:bg-gray-50'}`}
    >
      <div className="flex justify-between text-xs">
        <span className="font-semibold">
          v{version}
          {current ? ' · current' : ''}
        </span>
        <span className="text-gray-500">{formatDate(parseDate(step.mintedAt))}</span>
      </div>
      <div className="font-mono text-[10px] text-gray-400">{step.id.slice(0, 18)}…</div>
      <div className="mt-1 text-sm">
        {head ? <span className={isItalicRank(head.rank) ? 'italic font-semibold' : 'font-semibold'}>{head.name}</span> : '—'}
      </div>
      <div className="text-[11px] text-gray-500">{resolutionOf(step)}</div>
      {step.change && <div className="text-[11px] text-gray-600 mt-1">{driverLabel(step.change)}</div>}
      {(step.notes ?? []).length > 0 && <div className="text-[11px] text-amber-700 mt-1">Note from the spcfy team</div>}
    </button>
  )
}

const StepDetail: React.FC<{ step: SpinLineageStep; previous?: SpinLineageStep; version: number }> = ({ step, previous, version }) => {
  const change = step.change
  const diffs = previous ? sourceDiffs(step, previous) : []
  const ladders = (step.taxonomies ?? []).filter((l) => l.hitRank === 0)
  return (
    <div className="space-y-4 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="font-semibold">v{version}</h4>
          <Link to={`/spins/${step.id}`} className="link link-primary font-mono text-xs break-all">
            {step.id}
          </Link>
        </div>
        <span className="text-xs text-gray-500">
          {formatDate(parseDate(step.mintedAt))}
          {step.runReason ? ` · ${step.runReason}` : ''}
        </span>
      </div>

      {change ? (
        <div className="rounded border p-3 space-y-1">
          <div className="text-sm font-semibold">{driverLabel(change)}</div>
          <div className="text-sm">{effectLabel(change)}</div>
          {changeNotes(change).map((n) => (
            <div key={n} className="text-xs text-gray-500">
              {n}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">The oldest version on record.</p>
      )}

      {(step.notes ?? []).map((note) => (
        <div key={note} className="rounded bg-amber-50 text-amber-900 text-sm px-3 py-2">
          {note}
        </div>
      ))}

      {previous && (
        <div className="overflow-x-auto">
          <h5 className="text-sm font-semibold mb-1">
            Changes v{version - 1} → v{version}
          </h5>
          <table className="table table-xs">
            <thead>
              <tr>
                <th>Source</th>
                <th>Previous (v{version - 1})</th>
                <th>Current (v{version})</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {diffs.map((d) => (
                <tr key={d.source}>
                  <td>
                    <span className="inline-flex items-center gap-1.5">
                      <BlastSourceIcon source={d.source} size={16} />
                      {d.source}
                    </span>
                  </td>
                  <td>
                    <Name v={d.before} />
                  </td>
                  <td>
                    <Name v={d.after} />
                  </td>
                  <td>
                    <span className={`rounded px-2 py-0.5 text-[11px] ${STATUS_CLASS[d.status]}`}>{STATUS_LABEL[d.status]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="overflow-x-auto">
        <h5 className="text-sm font-semibold mb-1">Assignments at v{version}</h5>
        <table className="table table-xs">
          <thead>
            <tr>
              <th>Source</th>
              {RANK_LABELS.map((r) => (
                <th key={r}>{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ladders.map((l) => (
              <tr key={l.source}>
                <td>
                  <span className="inline-flex items-center gap-1.5">
                    <BlastSourceIcon source={l.source} size={16} />
                    {l.source}
                  </span>
                </td>
                {RANK_LABELS.map((r, i) => (
                  <td key={r} className={isItalicRank(i) ? 'italic' : ''}>
                    {cleanName(l.ranks?.[i]) ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** The SPIN's predecessor chain with what changed at each step. Loaded only when opened. */
export const SpinLineageModal: React.FC<{ spinId: string; isOpen: boolean; onClose: () => void }> = ({ spinId, isOpen, onClose }) => {
  const [lineage, setLineage] = useState<SpinLineage | null | undefined>(undefined)
  const [selected, setSelected] = useState(0)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setLineage(undefined)
    setSelected(0)
    fetchSpinLineage(spinId).then((l) => !cancelled && setLineage(l))
    return () => {
      cancelled = true
    }
  }, [spinId, isOpen])

  const steps = lineage?.steps ?? []
  const versionOf = (i: number) => steps.length - i

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} testId="spin-lineage-modal">
      <div className="modal-box w-11/12 max-w-6xl">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="text-lg font-semibold">SPIN lineage and taxonomic history</h3>
            <p className="text-xs text-gray-500">How the identification of this SPIN evolved over its predecessors.</p>
          </div>
          <button type="button" aria-label="Close" className="btn btn-ghost btn-sm btn-circle" onClick={onClose}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        {lineage === undefined && <p className="text-gray-500">Loading…</p>}
        {lineage === null && <p className="text-error">Lineage could not be loaded.</p>}
        {lineage && steps.length > 0 && (
          <div className="grid gap-4 md:grid-cols-[16rem_1fr]">
            <ol className="space-y-2">
              {steps.map((s, i) => (
                <li key={s.id}>
                  <StepCard step={s} version={versionOf(i)} current={i === 0} selected={i === selected} onSelect={() => setSelected(i)} />
                </li>
              ))}
              {lineage.truncated && <li className="text-xs text-gray-500">Older versions not shown.</li>}
            </ol>
            <StepDetail step={steps[selected]!} previous={steps[selected + 1]} version={versionOf(selected)} />
          </div>
        )}
      </div>
    </BaseModal>
  )
}
